/**
 * PARKED FEATURES: built, kept, and not shown.
 *
 * <h2>Why a flag and not a deletion</h2>
 *
 * A feature that is incomplete for one launch is not a feature that is wrong. Deleting it loses
 * the algorithms, the tests that pin them, and — most expensively — the measurements already
 * recorded against them, which is the only thing that stops the next person re-deriving the same
 * null result. So the code stays whole and the ENTRY POINTS are gated.
 *
 * <h2>Why one module rather than a flag per component</h2>
 *
 * The surfaces are scattered: a toolbar button, two admin study controls, and a dropdown in a
 * dialog three directories away. A boolean declared next to one of them is a boolean the others
 * will not find, and a half-parked feature — invisible in one place and live in another — is
 * worse than one that is simply on, because nobody can tell which state is intended.
 */

/**
 * TRENDPULSE. Parked 2026-10-04 for the B2B launch.
 *
 * <p>Flip to {@code true} to restore every TrendPulse surface at once. Nothing else has to change:
 * the processor, the shape detectors (double tops and bottoms, capitulation drops, exhaustion
 * spikes), the published-dataset reader, the chart overlays, the phase ribbon and the sub-pane
 * rendering are all untouched and still covered by their own tests, which run regardless of this
 * flag. The backend is not gated at all — {@code signalProvider=TRENDPULSE} still works on the API
 * and on the CLI, which is what keeps it developable while it is parked.
 *
 * <p>It is NOT finished. The detector thresholds were chosen to make the shapes selective rather
 * than derived from anything, the drift filter ran dead for its entire first existence, and the
 * ">90% win rate on extreme setups" premise could not be tested at the shipped thresholds. See
 * CLAUDE.md.
 */
export const TRENDPULSE_ENABLED = false;

/**
 * SUPERSEDED FOR THE TOOLBAR, 2026-10-04 (same day): TrendPulse is ADMIN-ONLY, not globally off.
 *
 * <p>The flag above still means what it says and still governs the DynaMan provider dropdown,
 * which has no profile of its own. What changed is the TOOLBAR requirement: hidden for the
 * production profiles, reachable for Admin. See {@code config/toolbarProfiles.js}, which owns
 * that rule — one narrower gate, and not one algorithm touched to get it either time.
 *
 * <p>Both live because they answer different questions. This file asks "is the feature shipped";
 * the profile module asks "may THIS reader see it". Collapsing them would mean the dropdown had
 * to know about roles.
 */
export const TRENDPULSE_PARKED_FOR_PRODUCTION = true;

/**
 * The toolbar actions that belong to TrendPulse.
 *
 * <p>Gated by ACTION rather than by editing each button definition, so a surface added later is
 * caught by adding one string here instead of by remembering a conditional at a fourth call site.
 *
 * <ul>
 *   <li>{@code run_trendpulse} — draws the curve and its sub-pane
 *   <li>{@code open_pattern_prediction_dialog} — scores shapes on that curve against price, and is
 *       inert without it ("run TrendPulse first" is its own disabled message)
 *   <li>{@code evaluate_validation_lines} — scores the curve's boundary shelves against ATR
 * </ul>
 *
 * <p>The last two are dependents, not separate features: both read the TrendPulse overlay's data
 * and can do nothing without it. Leaving them visible would show two controls whose only possible
 * outcome is an error telling the reader to run something they cannot see.
 */
export const TRENDPULSE_ACTIONS = new Set([
  'run_trendpulse',
  'open_pattern_prediction_dialog',
  'evaluate_validation_lines',
]);

/**
 * The DynaMan signal providers that are TrendPulse.
 *
 * <p>CONFLUENCE is on this list and that is deliberate. It is not a sibling of TrendPulse, it IS
 * TrendPulse: {@code ConfluenceSignalProvider} delegates to a real {@code TrendPulseProcessor} and
 * gates its shapes on CTS timing, so it needs the TrendPulse dataset and detects nothing without
 * it. Hiding one and showing the other would leave TrendPulse on screen under a different name —
 * and under the name whose measured result was a NULL one, where the co-occurrence lift between
 * CTS dates and TrendPulse shapes came out at 0.88-1.18x, i.e. no relationship at all.
 */
export const TRENDPULSE_PROVIDERS = new Set(['TRENDPULSE', 'CONFLUENCE']);

/**
 * Whether a toolbar action should be rendered.
 *
 * <p>DEPRECATED for the toolbar — {@code toolbarProfiles.isActionVisibleForProfile} is the gate
 * now, because visibility became a question about the READER rather than about the build. Kept,
 * exported and tested so a caller that has no profile in hand still has an answer, and so the
 * parked-feature concept survives independently of roles.
 */
export const isActionVisible = (action) =>
  TRENDPULSE_ENABLED || !TRENDPULSE_ACTIONS.has(action);

/** Whether a DynaMan signal provider should be offered. */
export const isProviderVisible = (provider) =>
  TRENDPULSE_ENABLED || !TRENDPULSE_PROVIDERS.has(provider);
